import { useEffect, useRef, memo } from "react";
import qs from "qs";
import { Dialog, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useInstantSearch } from "react-instantsearch";

import SearchBoxGeneric from "components/SearchBoxGeneric";
import InfiniteSearchResults from "components/SearchBoxGeneric/InfiniteSearchResults";
import AlgoliaSearchResultSkeleton from "components/AlgoliaSearchResultSkeleton";
import { Root } from "pages/Search/components/styles";
import { useRouter, useAlgolia } from "hooks";
import { appRoutes } from "routes";
import FilterSort from "components/FilterSort";
import Header from "pages/Search/components/Header";
import SearchInput from "pages/Search/components/SearchInput";
import SearchTerms from "pages/Search/components/SearchTerms";

const FilterSortWrapper = styled(Container)(({ theme }) => ({
  marginBottom: "20px",
  [theme.breakpoints.up("mobile")]: {
    marginBottom: "40px",
  },
}));

const View = memo(({ initialUiState, searchTerm, setSearchTerm, defaultFilters, defaultSortBy }) => {
  const { location, history } = useRouter();
  const { clearAllFiltersByPath } = useAlgolia();
  const canGoBack = useRef(true);
  const { setIndexUiState } = useInstantSearch();
  const filterSortRef = useRef(null);

  const replaceQueryString = (term) => {
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (term) params.term = term;
    else delete params.term;

    const newQueryString = Object.keys(params).length ? `?${qs.stringify(params)}` : "";
    const newPath = `${window.location.pathname}${newQueryString}`;

    window.history.replaceState({}, "", decodeURIComponent(newPath));
  };

  const onCloseDialog = () => {
    if (!canGoBack.current) {
      history.replace(appRoutes.rootPath());
    } else {
      history.goBack();
    }
  };

  const cleanSearch = () => {
    clearAllFiltersByPath({ path: appRoutes.searchPath() });
    setSearchTerm("");
    setIndexUiState(() => {
      return {
        ...initialUiState,
        configure: {
          ...initialUiState.configure,
          facetFilters: defaultFilters,
        },
        sortBy: defaultSortBy,
        query: "",
      };
    });
  };

  const handleDialogClose = () => {
    onCloseDialog();
    cleanSearch();
  };

  const handleOnChange = (event) => {
    const { value } = event.target;

    setSearchTerm(value);
    setIndexUiState((prev) => ({
      ...prev,
      query: value,
    }));
  };

  const handleReset = () => {
    filterSortRef.current.resetFilters();
    cleanSearch();
  };

  const handleSearchTermClick = (term) => {
    setSearchTerm(term);
    setIndexUiState((prev) => ({
      ...prev,
      query: term,
    }));
  };

  useEffect(() => {
    replaceQueryString(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (history.length <= 1) {
      canGoBack.current = false;
    }

    const unlisten = history.listen(() => {
      if (history.length <= 1) {
        canGoBack.current = false;
      } else {
        canGoBack.current = true;
      }
    });

    return () => unlisten();
  }, [history]);

  return (
    <>
      <Dialog fullScreen open>
        <Root>
          <SearchBoxGeneric
            headerSlot={<Header handleDialogClose={handleDialogClose} />}
            inputSlot={
              <SearchInput searchTerm={searchTerm} handleOnChange={handleOnChange} handleReset={handleReset} />
            }
            filtersSlot={
              <FilterSortWrapper>
                <FilterSort ref={filterSortRef} hidden={!searchTerm} configure={initialUiState?.configure} />
              </FilterSortWrapper>
            }
            emptySearchSlot={<SearchTerms onSearchTermClick={handleSearchTermClick} />}
            searchResultsSkeletonSlot={<AlgoliaSearchResultSkeleton />}
            resultsSlot={InfiniteSearchResults}
            initialQueryObject={initialUiState}
            isSearch
          />
        </Root>
      </Dialog>
    </>
  );
});

View.propTypes = {
  initialUiState: PropTypes.shape({ configure: PropTypes.shape({}) }).isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  defaultFilters: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  defaultSortBy: PropTypes.string.isRequired,
};

export default View;
